import { useForm } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MediaSelector } from '@/components/ui/media-selector';
import { BrandLookup } from '@/components/lookups/BrandLookup';
import { CountrySelect } from '@/components/lookups/CountrySelect';
import { ImageGalleryManager } from '@/components/ui/image-gallery-manager';
import { ITEM_STATUS_OPTIONS } from '@sizzlenz/shared';
import { useState, forwardRef, useImperativeHandle } from 'react';
import type { ApiItem } from '@/lib/api/types';
import type { LookupOption } from '@/components/ui/lookup';
import type { ApiEntity } from '@/lib/api/types';

interface ItemFormData {
  title: string;
  handle: string;
  description?: string;
  brandId?: number | string;
  image?: string;
  price_1?: string;
  price_2?: string;
  status: string;
  available: boolean;
  tags?: string;
  url?: string;
  galleryImages?: string[];
  country_origin?: string;
  country_manufactured?: string;
  countries_ingredients?: string[];
}

interface ItemFormProps {
  item?: ApiItem;
  initialValues?: Partial<ItemFormData>;
  onSubmit: (data: ItemFormData) => void;
}

export interface ItemFormRef {
  submit: () => void;
}

export const ItemForm = forwardRef<ItemFormRef, ItemFormProps>(
  ({ item, initialValues, onSubmit }, ref) => {
    const [selectedBrand, setSelectedBrand] = useState<LookupOption<ApiEntity> | null>(
      item?.brand
        ? {
            id: item.brand.id,
            label: item.brand.name_1,
            data: item.brand as unknown as ApiEntity,
          }
        : null
    );

    // Derive initialBrandId for the BrandLookup to auto-resolve
    const initialBrandId =
      !item?.brand && initialValues?.brandId ? initialValues.brandId : undefined;

    const [galleryImages, setGalleryImages] = useState<string[]>(
      (item?.data?.gallery as any)?.images || []
    );
    const [countryOrigin, setCountryOrigin] = useState<string | null>(
      (item?.data as any)?.country_origin || null
    );
    const [countryManufactured, setCountryManufactured] = useState<string | null>(
      (item?.data as any)?.country_manufactured || null
    );
    const [countriesIngredients, setCountriesIngredients] = useState<string[]>(
      (item?.data as any)?.countries_ingredients || []
    );
    console.log({ countryOrigin, countryManufactured });
    const {
      register,
      handleSubmit,
      setValue,
      watch,
      formState: { errors },
    } = useForm<ItemFormData>({
      defaultValues: {
        title: item?.title || initialValues?.title || '',
        handle: item?.handle || initialValues?.handle || '',
        description: item?.description || initialValues?.description || '',
        brandId: item?.brand?.id || item?.brandId || initialValues?.brandId,
        image: item?.image || initialValues?.image || '',
        price_1: item?.price_1 ? String(item.price_1) : initialValues?.price_1 || '',
        price_2: item?.price_2 ? String(item.price_2) : initialValues?.price_2 || '',
        status: item?.status || initialValues?.status || 'active',
        available: item?.available ?? initialValues?.available ?? true,
        tags:
          item?.tags?.filter((tag) => !tag.startsWith('@')).join(', ') || initialValues?.tags || '',
        url: item?.url || (item?.data as any)?.url || initialValues?.url || '',
      },
    });

    const imageValue = watch('image');
    const statusValue = watch('status');

    const handleFormSubmit = (data: ItemFormData) => {
      onSubmit({
        ...data,
        brandId: selectedBrand?.id ?? undefined,
        galleryImages,
        country_origin: countryOrigin || undefined,
        country_manufactured: countryManufactured || undefined,
        countries_ingredients: countriesIngredients.length > 0 ? countriesIngredients : undefined,
      });
    };

    // Expose submit method to parent via ref
    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(handleFormSubmit)();
      },
    }));

    return (
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        {/* Title */}
        <div>
          <Label htmlFor="title">
            Title <span className="text-red-500">*</span>
          </Label>
          <Input
            id="title"
            {...register('title', { required: 'Title is required' })}
            placeholder="Enter product title"
          />
          {errors.title && <p className="text-sm text-red-600 mt-1">{errors.title.message}</p>}
        </div>

        {/* Handle */}
        <div>
          <Label htmlFor="handle">Handle</Label>
          <Input id="handle" {...register('handle')} placeholder="product-handle" />
          {errors.handle && <p className="text-sm text-red-600 mt-1">{errors.handle.message}</p>}
        </div>

        {/* Brand */}
        <div>
          <Label htmlFor="brand">Brand</Label>
          <BrandLookup
            value={selectedBrand}
            onChange={(brand) => {
              setSelectedBrand(brand);
              setValue('brandId', brand?.id ?? undefined);
            }}
            placeholder="Select a brand"
            initialId={initialBrandId}
          />
        </div>

        {/* Description */}
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            {...register('description')}
            placeholder="Enter product description"
            rows={4}
          />
        </div>

        {/* Image */}
        <div>
          <MediaSelector
            value={imageValue || null}
            onChange={(url) => setValue('image', typeof url === 'string' ? url : url?.[0] || '')}
            label="Image"
          />
        </div>

        {/* URL */}
        <div>
          <Label htmlFor="url">Product URL</Label>
          <Input
            id="url"
            {...register('url')}
            placeholder="https://example.com/product"
            type="url"
          />
        </div>

        {/* Prices */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="price_1">Price</Label>
            <Input
              id="price_1"
              {...register('price_1')}
              placeholder="0.00"
              type="number"
              step="0.01"
            />
          </div>
          <div>
            <Label htmlFor="price_2">Compare Price</Label>
            <Input
              id="price_2"
              {...register('price_2')}
              placeholder="0.00"
              type="number"
              step="0.01"
            />
          </div>
        </div>

        {/* Status */}
        <div>
          <Label htmlFor="status">Status</Label>
          <Select value={statusValue} onValueChange={(value) => setValue('status', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              {ITEM_STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Available */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="available"
            {...register('available')}
            className="w-4 h-4 rounded border-gray-300 cursor-pointer"
          />
          <Label htmlFor="available" className="cursor-pointer">
            Available for purchase
          </Label>
        </div>

        {/* Tags */}
        <div>
          <Label htmlFor="tags">Tags</Label>
          <Input
            id="tags"
            {...register('tags')}
            placeholder="organic, gluten-free, vegan (comma-separated)"
          />
          <p className="text-sm text-gray-500 mt-1">Separate tags with commas</p>
        </div>

        {/* Country of Origin */}
        <div>
          <Label>Country of Origin</Label>
          <CountrySelect
            value={countryOrigin}
            onValueChange={(val) => setCountryOrigin(val as string)}
            placeholder="Select country of origin..."
          />
        </div>

        {/* Country of Manufacture */}
        <div>
          <Label>Country of Manufacture</Label>
          <CountrySelect
            value={countryManufactured}
            onValueChange={(val) => setCountryManufactured(val as string)}
            placeholder="Select country of manufacture..."
          />
        </div>

        {/* Ingredient Origins Countries */}
        <div>
          <Label>Ingredient Origins Countries</Label>
          <CountrySelect
            mode="multiple"
            value={countriesIngredients}
            onValueChange={(val) => setCountriesIngredients(val as string[])}
            placeholder="Select ingredient origin countries..."
          />
        </div>

        {/* Gallery Media */}
        <ImageGalleryManager
          images={galleryImages}
          onImagesChange={setGalleryImages}
          title="Gallery Media"
          description="Add images to showcase your product"
          emptyStateMessage="No images added yet"
          gridCols={3}
        />
      </form>
    );
  }
);
